import React, { useState, useEffect } from "react";
import { USER } from "../../config/constants";
import Loader from "../../components/Loader/Loader.js";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import { esES } from "@mui/material/locale";
import { FilterOutlined } from "@ant-design/icons";
import DeleteOutlinedIcon from "@mui/icons-material/DeleteOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import BusinessOutlinedIcon from "@mui/icons-material/BusinessOutlined";
import moment from "moment/moment";
import axios from "axios";
import { BACKEND_URL } from "../../config/config";
import MarkEmailReadIcon from "@mui/icons-material/MarkEmailRead";
import DoneAllIcon from "@mui/icons-material/DoneAll";
import DeleteSweepOutlinedIcon from "@mui/icons-material/DeleteSweepOutlined";
import MaterialTable, { MTableToolbar } from "@material-table/core";
import { ExportPdf } from "@material-table/exporters";
import { ExportCsv } from "@material-table/exporters";
import { Button, Popconfirm } from "antd";
import { useNavigate } from "react-router-dom";
import Modal from "../../components/Modal/Modal";
import { OpenModalView } from "../../components/Account";
import { useNotification } from "../../context/NotificationContext.js";
import "./Notifications.css";

export function Notifications() {
  const currentUser = JSON.parse(localStorage.getItem(USER));
  const navigate = useNavigate();
  const [notificationsData, setNotificationsData] = useState([]);
  const [filtering, setFiltering] = React.useState(false);
  const [reload, setReload] = useState(false);
  const [isVisibleModal, setIsVisibleModal] = useState(false);
  const [modalTitle, setModalTitle] = useState("");
  const [modalContent, setModalContent] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  const getUnreadNotifications = async (user) => {
    if (user?._id) {
      setIsLoading(true);
      const url = `${BACKEND_URL}/customerNotifications?id=${user?._id}&show=NOREADED`; // + currentUser._id;

      const notificationsData = await axios
        .get(url)
        .catch((err) => console.log(err));

      if (notificationsData?.data) {
        const payload = notificationsData?.data?.notifications || [];

        setNotificationsData(payload);

        setIsLoading(false);
      } else {
        setIsLoading(false);
        console.log("No dashboard data retrieved");
      }
    }
  };

  const { notification, updateNotification } = useNotification();

  // Bell badge count (MenuTop) only re-fetches when this same context value
  // changes - normally driven by an incoming FCM push. Actions taken here
  // (read/delete, one or all) change the count too, so they need to poke the
  // same signal or the badge goes stale until the next real push arrives.
  const notifyBadgeChanged = () => updateNotification("notifications-changed-" + Date.now());

  useEffect(() => {
    if (notification) {
      setReload(!reload);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [notification]);

  useEffect(() => {
    getUnreadNotifications(currentUser);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reload]);

  const theme = createTheme(
    {
      palette: {
        primary: { main: "#1976d2" },
      },
    },
    esES
  );

  const createAction = () => {};

  const columns = [
    {
      title: "Subject",
      field: "subject",
      cellStyle: { width: "24%" },
      headerStyle: { width: "24%" },
    },
    {
      title: "Description",
      field: "description",
      cellStyle: { width: "34%" },
      headerStyle: { width: "34%" },
    },
    {
      title: "Company",
      field: "lead",
      render: (rowData) => (
        <span className="table-entity">
          <span className="table-entity__icon">
            <BusinessOutlinedIcon />
          </span>
          <span className="table-entity__text">
            {rowData?.account?.accountName || "-"}
          </span>
        </span>
      ),
      cellStyle: { width: "24%" },
      headerStyle: { width: "24%" },
    },
    {
      title: "Created",
      field: "created",
      render: (rowData) => (
        <span>{moment(rowData?.created).format("MM/DD/YYYY")}</span>
      ),
      cellStyle: { width: "120px", whiteSpace: "nowrap" },
      headerStyle: { width: "120px", whiteSpace: "nowrap" },
    },
  ];

  const onMarkAsReadClick = async (event, rowData) => {
    setIsLoading(true);
    const url = `${BACKEND_URL}/updateCustomerNotification`;

    rowData.readed = true;

    // eslint-disable-next-line no-unused-vars
    const updatedNotification = await axios
      .put(url, rowData)
      .catch((err) => console.log(err));

    setIsLoading(false);

    getUnreadNotifications(currentUser);
    notifyBadgeChanged();
  };

  const onDeleteNotification = async (event, rowData) => {
    setIsLoading(true);
    const url = `${BACKEND_URL}/deleteCustomerNotification?id=` + rowData._id;

    // eslint-disable-next-line no-unused-vars
    const notificationsData = await axios
      .post(url)
      .catch((err) => console.log(err));
    setIsLoading(false);
    getUnreadNotifications(currentUser);
    notifyBadgeChanged();
  };

  const onMarkAllAsReadClick = async () => {
    setIsLoading(true);
    const url = `${BACKEND_URL}/markAllCustomerNotificationsAsRead`;

    await axios
      .post(url, { contact: currentUser._id })
      .catch((err) => console.log(err));

    setIsLoading(false);
    getUnreadNotifications(currentUser);
    notifyBadgeChanged();
  };

  const onDeleteAllClick = async () => {
    setIsLoading(true);
    const url = `${BACKEND_URL}/deleteAllCustomerNotifications`;

    await axios
      .post(url, { contact: currentUser._id })
      .catch((err) => console.log(err));

    setIsLoading(false);
    notifyBadgeChanged();
    getUnreadNotifications(currentUser);
  };

  const getRowStyle = (rowData) => {
    return rowData.readed ? "unread-row" : "unread-row";
  };

  // Navigates to the list page for the entity this notification is about
  // (no per-id detail routes exist in this portal - the target page reads
  // the id from navigation state and opens that specific row, e.g. Contracts
  // auto-opening the Approve modal for openContractId), falling back to the
  // generic Account/Lead modal for notifications with no such reference
  // (e.g. RPH/dispatch-only notifications).
  const onView = (data) => {
    if (!data.readed) {
      onMarkAsReadClick(null, data);
    }

    const parcAgreementHistoryId = data?.parcAgreementHistory?._id || data?.parcAgreementHistory;
    const opportunityId = data?.opportunity?._id || data?.opportunity;
    const invoiceId = data?.invoice?._id || data?.invoice;

    if (parcAgreementHistoryId) {
      return navigate("/contracts", { state: { openContractId: parcAgreementHistoryId } });
    }
    if (opportunityId) {
      return navigate("/proposals", { state: { openOpportunityId: opportunityId } });
    }
    if (invoiceId) {
      return navigate("/invoices", { state: { openInvoiceId: invoiceId } });
    }

    setIsVisibleModal(true);
    setModalTitle("Child / Site Account Information");
    setModalContent(
      <>
        <OpenModalView data={data} />
      </>
    );
  };

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  return (
    <div
      style={{ display: "flex", flexDirection: "column", paddingTop: "1rem" }}
    >
      {isLoading ? (
        <Loader />
      ) : (
        <div className="listContainer">
          <ThemeProvider theme={theme}>
            <MaterialTable
              columns={columns}
              className="genericTable"
              components={{
                Toolbar: (props) => (
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "flex-end",
                      alignItems: "center",
                      marginRight: "8px",
                    }}
                  >
                    <div style={{ width: "100%" }}>
                      <MTableToolbar {...props} />
                    </div>
                    <Popconfirm
                      title="Mark all notifications as read?"
                      onConfirm={onMarkAllAsReadClick}
                      okText="Yes"
                      cancelText="No"
                      disabled={!notificationsData.length}
                    >
                      <Button disabled={!notificationsData.length} title="Mark all as read">
                        <DoneAllIcon fontSize="small" />
                      </Button>
                    </Popconfirm>
                    <Popconfirm
                      title="Delete all notifications?"
                      onConfirm={onDeleteAllClick}
                      okText="Yes"
                      cancelText="No"
                      disabled={!notificationsData.length}
                    >
                      <Button disabled={!notificationsData.length} title="Delete all" danger>
                        <DeleteSweepOutlinedIcon fontSize="small" />
                      </Button>
                    </Popconfirm>
                    <Button
                      onClick={() => {
                        setFiltering((currentFilter) => !currentFilter);
                      }}
                    >
                      <FilterOutlined />
                    </Button>
                  </div>
                ),
              }}
              data={notificationsData}
              actions={[
                (rowData) => ({
                  icon: () => (
                    <VisibilityOutlinedIcon className="table-action-icon table-action-icon--view" />
                  ),
                  tooltip: "View",
                  onClick: (event, rowData) => {
                    onView(rowData);
                  },
                }),
                (rowData) => ({
                  icon: () => (
                    <MarkEmailReadIcon className="table-action-icon table-action-icon--payment" />
                  ),
                  tooltip: "Mark as read",
                  onClick: onMarkAsReadClick,
                }),

                (rowData) => ({
                  icon: () => (
                    <DeleteOutlinedIcon className="table-action-icon table-action-icon--danger" />
                  ),
                  tooltip: "Delete",
                  onClick: onDeleteNotification,
                }),
              ]}
              options={{
                rowClassName: (rowData) => getRowStyle(rowData),
                actionsColumnIndex: -1,
                actionsCellStyle: {
                  width: 128,
                  minWidth: 128,
                  maxWidth: 128,
                  whiteSpace: "nowrap",
                  textAlign: "center",
                },
                pageSize: 10,
                filtering,
                exportMenu: [
                  {
                    label: "Export tp PDF",
                    exportFunc: (cols, datas) =>
                      ExportPdf(cols, datas, "Notifications Report - "+new Date().toISOString()),
                  },
                  {
                    label: "Export to CSV",
                    exportFunc: (cols, datas) =>
                      ExportCsv(cols, datas, "Notifications Report - "+new Date().toISOString()),
                  },
                ],
              }}
              style={{ width: "100%" }}
              title="Notifications"
              onCreateButtonClick={createAction}
            />
          </ThemeProvider>
        </div>
      )}
      <Modal
        title={modalTitle}
        open={isVisibleModal}
        setIsVisible={setIsVisibleModal}
        className={"openModalDocumentsList"}
      >
        {modalContent}
      </Modal>
    </div>
  );
}
